import dayjs from 'dayjs';

// Constants
import { ONE_DAY } from '@/constants';

//  Types
import { DisplayCheckResult } from '@/types';

/**
 * Check if cached data has expired based on last fetched timestamp
 * @param fetchTimestamp - Timestamp when data was last fetched (in milliseconds)
 * @param maxAge - Cache duration in milliseconds
 * @returns true if cache has expired, false otherwise
 */
export const isCacheExpired = (
  fetchTimestamp: number,
  maxAge: number,
): boolean => {
  return Date.now() - fetchTimestamp > maxAge;
};

export type DateStatus<T = object> = T & {
  date: Date;
};

export const findNearestStatusDate = <T extends object>(
  activityDates: DateStatus<T>[],
): DateStatus<T> | null => {
  if (!activityDates.length) return null;

  const today = dayjs();

  return activityDates.reduce((nearest, current) => {
    const currentDiff = Math.abs(today.diff(dayjs(current.date), 'second'));
    const nearestDiff = Math.abs(today.diff(dayjs(nearest.date), 'second'));

    return currentDiff < nearestDiff ? current : nearest;
  });
};

/**
 * Returns the relative duration between a given date and today in compact format (y, m, d).
 * @param activityDates - Object containing a `date` field
 * @returns Compact duration like "2y3m", "5m10d", "In 3m", or "Today"
 */
export const getRelativeDuration = <T extends object>(
  activityDates?: DateStatus<T> | null,
): string => {
  if (!activityDates?.date) return '';

  const date = dayjs(activityDates.date);
  const today = dayjs();

  if (date.isSame(today, 'day')) return 'Today';

  const isFuture = date.isAfter(today);
  const start = isFuture ? today : date;
  const end = isFuture ? date : today;

  const years = end.diff(start, 'year');
  const months = end.diff(start.add(years, 'year'), 'month');
  const days = end.diff(start.add(years, 'year').add(months, 'month'), 'day');

  const parts = [
    years ? `${years}y` : '',
    months ? `${months}m` : '',
    days ? `${days}d` : '',
  ].filter(Boolean);

  if (!parts.length) return 'Today';

  const result = parts.join('');
  return isFuture ? `In ${result}` : result;
};

/**
 * Calculate the duration from a specific date to now in compact format
 * @param startDate - The start date (ISO string or Date)
 * @returns Compact duration string in format '{years}y{months}m' (e.g., '10y11m', '0y6m', '2y0m')
 */
export const getCompactDuration = (startDate: string | Date): string => {
  if (!startDate) return '';

  const start = dayjs(startDate);
  const now = dayjs();

  // Calculate years and months
  const years = now.diff(start, 'year');
  const monthsTotal = now.diff(start, 'month');
  const months = monthsTotal % 12;

  return `${years}y${months}m`;
};

/**
 * Check if birthday message should be shown for 24 hours from the start of the birthday day
 * @param birthday - The birthday date (ISO string or Date)
 * @returns Object with shouldShow boolean and msUntilHide (milliseconds until hide, or null if not showing)
 */
export const checkBirthdayDisplay = (birthday?: string): DisplayCheckResult => {
  if (!birthday) {
    return { isDisplay: false, timeoutMs: null };
  }

  const today = dayjs();

  const birthDate = dayjs(birthday);

  // Check if today is the birthday
  const isBirthday =
    today.month() === birthDate.month() && today.date() === birthDate.date();

  if (!isBirthday) {
    return { isDisplay: false, timeoutMs: null };
  }

  // Calculate when the birthday period started (start of today)
  const birthdayStart = today.startOf('day');

  const hoursSinceStart = today.diff(birthdayStart, 'hour', true);

  // Show birthday message if less than 24 hours have passed since the start of the day
  if (hoursSinceStart < ONE_DAY) {
    // Calculate milliseconds until 24 hours have passed
    const msUntil24Hours = (ONE_DAY - hoursSinceStart) * 60 * 60 * 1000;
    return { isDisplay: true, timeoutMs: msUntil24Hours };
  }

  return { isDisplay: false, timeoutMs: null };
};

/**
 * Checks if a given date is today.
 * @param date The date to check.
 * @return True if the date is today, false otherwise.
 */
export const isToday = (date: string | undefined) => {
  if (!date) return false;

  return dayjs(date).isSame(dayjs(), 'day');
};

/**
 * Check if anniversary message should be shown within a 7-day window
 * (3 days before and 3 days after the onboarding anniversary)
 * @param onboardedAt - The onboarding date (ISO string or Date)
 * @returns Object with shouldShow boolean and msUntilHide (milliseconds until hide, or null if not showing)
 */
export const checkAnniversaryDisplay = (
  onboardedAt?: string,
): DisplayCheckResult => {
  if (!onboardedAt) {
    return { isDisplay: false, timeoutMs: null };
  }

  const onboardingDate = dayjs(onboardedAt);
  if (!onboardingDate.isValid()) {
    return { isDisplay: false, timeoutMs: null };
  }

  const today = dayjs();
  const candidateYears = [today.year() - 1, today.year(), today.year() + 1];

  for (const year of candidateYears) {
    const anniversary = onboardingDate.year(year);
    const rangeStart = anniversary.subtract(3, 'day').startOf('day');
    const rangeEnd = anniversary.add(3, 'day').endOf('day');

    const isWithinRange =
      (today.isAfter(rangeStart) || today.isSame(rangeStart)) &&
      (today.isBefore(rangeEnd) || today.isSame(rangeEnd));

    if (isWithinRange) {
      const timeoutMs = Math.max(rangeEnd.diff(today), 0);
      return { isDisplay: true, timeoutMs };
    }
  }

  return { isDisplay: false, timeoutMs: null };
};

/**
 * Check if the onboarding date is upcoming today
 * @param date - The date of the onboarding (ISO string or Date)
 * @returns True if the onboarding date is today, false otherwise
 */
export const checkCelebrationComing = (date: string): boolean => {
  const today = dayjs();
  const onboardedDate = dayjs(date);

  return (
    onboardedDate.date() === today.date() &&
    onboardedDate.month() === today.month() &&
    onboardedDate.year() === today.year()
  );
};

/**
 * Normalizes a start date for training form.
 * If the selected date is today, returns current time.
 * Otherwise, sets time to 8:00 AM.
 * @param selectedDate - The date selected by the user
 * @returns ISO string of the normalized date, or empty string if no date provided or invalid
 */
export const normalizeStartDate = (selectedDate?: Date): string => {
  if (!selectedDate) return '';

  const date = dayjs(selectedDate);
  if (!date.isValid()) return '';

  const isToday = date.isSame(dayjs(), 'day');
  const normalizedDate = isToday
    ? new Date()
    : date.hour(8).minute(0).second(0).millisecond(0).toDate();

  return normalizedDate.toISOString();
};

/**
 * Normalizes an end date for training form.
 * If the selected date is today, returns current time.
 * Otherwise, sets time to 5:30 PM.
 * @param selectedDate - The date selected by the user
 * @returns ISO string of the normalized date, or empty string if no date provided or invalid
 */
export const normalizeEndDate = (selectedDate?: Date): string => {
  if (!selectedDate) return '';

  const date = dayjs(selectedDate);
  if (!date.isValid()) return '';

  const isToday = date.isSame(dayjs(), 'day');
  const normalizedDate = isToday
    ? new Date()
    : date.hour(17).minute(30).second(0).millisecond(0).toDate();

  return normalizedDate.toISOString();
};

export const checkFutureDate = (dateStr: string) => {
  const inputDate = dayjs(dateStr);
  const now = dayjs();
  return inputDate.isAfter(now, 'day');
};

export const gmt7ToUtcIso24 = (timeStr: string) => {
  // Accept "H:mm", "HH:mm", "H:mm:ss", "HH:mm:ss"
  const re = /^\s*(\d{1,2}):(\d{2})(?::(\d{2}))?\s*$/;
  const m = timeStr.match(re);
  if (!m)
    throw new Error(
      'Invalid time format. Use "HH:mm" or "H:mm" or include seconds "HH:mm:ss".',
    );

  const hour = parseInt(m[1]!, 10);
  const minute = parseInt(m[2]!, 10);
  const second = parseInt(m[3] ?? '0', 10);

  if (
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59 ||
    second < 0 ||
    second > 59
  ) {
    throw new Error('Time values out of range.');
  }

  // Convert GMT+7 -> UTC by subtracting 7 hours
  const utcHour = (hour - 7 + 24) % 24;

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(utcHour)}:${pad(minute)}:${pad(second)}.000Z`;
};

/**
 * Converts a project event datetime (GMT+7) to UTC ISO format for API payloads.
 * Accepts values like "YYYY-MM-DDTHH:mm:ssZ" and preserves the date part.
 */
export const normalizeProjectEventDateTime = (value: string): string => {
  const UTC_SUFFIX = /Z$/;
  const TIMEZONE_OFFSET = /[+-]\d{2}:\d{2}$/;
  const hasTime = value.includes('T');
  const [datePart, rawTimePart = hasTime ? '00:00:00' : '08:00:00'] =
    value.split('T');
  if (!datePart) return value;

  const timePart =
    rawTimePart
      .replace(UTC_SUFFIX, '')
      .replace(TIMEZONE_OFFSET, '')
      .split('.')?.[0] || (hasTime ? '00:00:00' : '08:00:00');
  return `${datePart}T${gmt7ToUtcIso24(timePart)}`;
};

/**
 * Resolves the `occurredAt` UTC string for a project event datetime.
 * Returns `undefined` when the datetime is today's date with no specific time,
 * since no explicit scheduling is needed in that case.
 */
export const resolveEventOccurredAt = (
  dateTime: string | undefined,
): string | undefined => {
  if (!dateTime) return undefined;

  const NEAR_CURRENT_TIME_THRESHOLD_MINUTES = 1;
  const hasSpecificTime = dateTime.includes('T');
  const isDateToday = isToday(dateTime);

  if (!hasSpecificTime && isDateToday) return undefined;

  if (
    hasSpecificTime &&
    Math.abs(dayjs().diff(dayjs(dateTime), 'minute')) <=
      NEAR_CURRENT_TIME_THRESHOLD_MINUTES
  )
    return undefined;

  return normalizeProjectEventDateTime(dateTime);
};

/**
 *
 * @param value: YYYY-MM-DD
 * Output: 2025-10-30 + T07:24:49.510Z
 */
export const formalizeTrainingDate = (value: string, isEndDate = false) => {
  const currentTime = new Date().toISOString().split('T')[1];
  const isFutureDate = checkFutureDate(value);
  const isIncludeTime = value.includes('T');

  // If the date is in the future, set time to 01:00:00.000Z = 08:00AM+7, endDate time will be 05:30PM+7
  return isIncludeTime
    ? `${value.split('T')?.[0]}T${gmt7ToUtcIso24(value.slice(11, 19))}`
    : new Date(
        `${value}T${isFutureDate ? (!isEndDate ? '01:00:00.000Z' : '10:30:00.000Z') : currentTime}`,
      ).toISOString();
};

export const isMatchedDateTime = (date1: string, date2: string) => {
  // Compare without time
  if (!date1.includes('T') || !date2.includes('T')) {
    return date1.slice(0, 10) === date2.slice(0, 10);
  }

  return date1.slice(0, 16) === date2.slice(0, 16);
};
